import { ChartColumn } from 'lucide-react';
import type { ReactNode } from 'react';
import { DashboardCard, type CardError } from '~/components/dashboard/DashboardCard';
import { EmptyState } from '~/components/ui';

/** Props every chart card shares. */
export interface ChartCardProps {
  title: string;
  /** One line under the title. */
  description?: string;
  /** Required: says what the chart shows ("Videos created, last 30 days"); the figures also go in `summary`. */
  ariaLabel: string;
  loading?: boolean;
  /** A persistent load failure; it replaces the chart and keeps the header. */
  error?: CardError;
  /** Empty-state copy: what is missing, and what produces data. The header stays. */
  emptyTitle: string;
  emptyDescription: string;
  /** The chart's figures as text, under it. */
  summary?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

interface ChartFrameProps extends ChartCardProps {
  isEmpty: boolean;
  /** Size classes of the chart box. */
  boxClass?: string;
  legend?: ReactNode;
  /** Replaces the default stacked layout (the donut sits its legend beside the chart). */
  layout?: string;
  children: ReactNode;
}

/** The shell the chart cards render: `DashboardCard` header, then error, empty state or the chart (DESIGN.md §8.1). */
export function ChartFrame({
  title,
  description,
  ariaLabel,
  loading = false,
  error,
  emptyTitle,
  emptyDescription,
  summary,
  actions,
  className = '',
  isEmpty,
  boxClass = 'w-full h-72',
  legend,
  layout = 'space-y-4',
  children,
}: ChartFrameProps) {
  return (
    <DashboardCard
      loading={loading}
      skeleton="chart"
      className={className}
      title={title}
      error={error}
      headerProps={{ description, actions }}
    >
      {isEmpty ? (
        <EmptyState
          icon={<ChartColumn className="size-8" aria-hidden="true" />}
          title={emptyTitle}
          description={emptyDescription}
        />
      ) : (
        <div className={layout}>
          <div role="img" aria-label={ariaLabel} className={`relative ${boxClass}`}>
            {children}
          </div>
          {legend}
          {summary && <p className="text-xs text-gray-500 dark:text-gray-400">{summary}</p>}
        </div>
      )}
    </DashboardCard>
  );
}
